import {
  ensureEchoConnectionBound,
  subscribeEchoConnection,
} from "@/lib/echo-connection";
import { logger } from "@/lib/logger";
import { driverDispatchChannel } from "@/lib/services/driver-dispatch-channel";
import { notificationFallbackSync } from "@/lib/services/notification-fallback-sync";
import { useAuthStore } from "@/stores/use-auth-store";
import * as Location from "expo-location";
import * as TaskManager from "expo-task-manager";
import { Platform } from "react-native";
import api from "../api";
import { getEcho } from "../echo";
import { webSocketService } from "./websocket";

const LOCATION_TASK_NAME = "background-location-task";

export interface LocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  altitude?: number | null;
  heading: number | null;
  speed: number;
}

// Define the background location task at module level
// This must be defined before the service class is instantiated
// Only define if TaskManager is available (native module is linked)
try {
  if (
    TaskManager.isTaskDefined &&
    typeof TaskManager.isTaskDefined === "function"
  ) {
    if (!TaskManager.isTaskDefined(LOCATION_TASK_NAME)) {
      TaskManager.defineTask(LOCATION_TASK_NAME, async ({ data, error }) => {
        if (error) {
          logger.error("Background location task error:", error);
          return;
        }

        logger.debug("location task data", data);

        if (data) {
          const { locations } = data as any;
          if (locations && locations.length > 0) {
            const location = locations[0];
            const coordinates: LocationCoordinates = {
              latitude: location.coords.latitude,
              longitude: location.coords.longitude,
              accuracy: location.coords.accuracy,
              altitude: location.coords.altitude,
              heading: location.coords.heading,
              speed: location.coords.speed,
            };

            const bookingId = useAuthStore.getState().activeBooking;
            // Get the stored booking ID from global variable
            if (bookingId) {
              // Call the service method to handle the update
              // We need to access the service instance
              if ((global as any).locationTrackingServiceInstance) {
                await (
                  global as any
                ).locationTrackingServiceInstance.handleBackgroundLocationUpdate(
                  bookingId,
                  coordinates,
                );
              }
            }
          }
        }
      });
    }
  }
} catch (error) {
  logger.warn(
    "TaskManager not available. Background location tracking will not work until app is rebuilt with native modules:",
    error,
  );
}

class LocationTrackingService {
  private locationSubscription: Location.LocationSubscription | null = null;
  private webWatchId: number | null = null;
  public lastLocation: LocationCoordinates | null = null;
  private lastUpdateTime: number = 0;
  private updateIntervalKm: number = 0.03; // Send update every 30 meters
  private minUpdateIntervalMs: number = 5000; // Minimum 5 seconds between updates
  private isTracking: boolean = false;
  private isBackgroundTracking: boolean = false;
  private locationBatch: LocationCoordinates[] = [];
  private lastApiUpdateTime: number = 0;
  private apiErrorCount: number = 0;
  private readonly GENERAL_API_THROTTLE_MS = 60000; // 1 minute for general
  private readonly BOOKING_API_THROTTLE_MS = 15000; // 15 seconds for active booking
  private connectionUnsubscribe: (() => void) | null = null;

  /**
   * Connect the driver dispatch channel for job offers.
   */
  connectDispatch(): void {
    const userId = useAuthStore.getState().user?.id;
    const echoRef = getEcho();

    if (!echoRef) {
      logger.error("Echo not available for driver dispatch channel");
      return;
    }

    ensureEchoConnectionBound(echoRef);

    if (!this.connectionUnsubscribe) {
      this.connectionUnsubscribe = subscribeEchoConnection({
        onConnected: () => {
          notificationFallbackSync.sync().catch(() => {});

          const currentUserId = useAuthStore.getState().user?.id;
          if (currentUserId) {
            driverDispatchChannel.connect(currentUserId);
          }
        },
      });
    }

    if (userId) {
      driverDispatchChannel.connect(userId);
    }
  }

  /**
   * Disconnect the driver dispatch channel.
   */
  disconnectDispatch(): void {
    driverDispatchChannel.disconnect();
    notificationFallbackSync.stopDriverPolling();

    if (this.connectionUnsubscribe) {
      this.connectionUnsubscribe();
      this.connectionUnsubscribe = null;
    }
  }

  /**
   * Check if API update should be allowed based on status and backoff
   */
  private shouldSendToApi(type: "general" | "booking"): boolean {
    const now = Date.now();
    const timeSinceLastApi = now - this.lastApiUpdateTime;

    // Base throttle based on type
    const baseThrottle =
      type === "booking"
        ? this.BOOKING_API_THROTTLE_MS
        : this.GENERAL_API_THROTTLE_MS;

    // Exponential backoff if there are errors (cap at 5 minutes)
    const backoffFactor = Math.min(Math.pow(2, this.apiErrorCount), 20);
    const requiredDelay = baseThrottle * backoffFactor;

    return timeSinceLastApi >= requiredDelay;
  }

  async sendLocation(location: LocationCoordinates) {
    if (driverDispatchChannel.sendGeneralLocation(location)) {
      logger.debug("Location updated via general WebSocket");
      return true;
    }

    if (!this.shouldSendToApi("general")) {
      return false;
    }

    try {
      await api.post("/location/update-general", location);
      this.lastApiUpdateTime = Date.now();
      this.apiErrorCount = 0;
      logger.debug("Location updated via general API fallback");
      return true;
    } catch (error) {
      this.apiErrorCount++;
      logger.error("Failed to update general location on backend:", error);
      return false;
    }
  }

  /**
   * Request location permissions
   */
  async requestPermissions(): Promise<boolean> {
    try {
      const { status: foregroundStatus } =
        await Location.requestForegroundPermissionsAsync();

      if (foregroundStatus !== "granted") {
        logger.debug("Foreground location permission denied");
        return false;
      }

      // Request background location permission for tracking while app is in background
      const { status: backgroundStatus } =
        await Location.requestBackgroundPermissionsAsync();

      if (backgroundStatus !== "granted") {
        logger.debug("Background location permission denied");
        // return false;
      }

      return true;
    } catch (error) {
      logger.error("Error requesting location permissions:", error);
      return false;
    }
  }

  /**
   * Get current location once
   */
  async getCurrentLocation(): Promise<LocationCoordinates | null> {
    try {
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.High,
      });

      return {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        accuracy: location.coords.accuracy,
        altitude: location.coords.altitude,
        heading: location.coords.heading,
        speed: location.coords.speed || 0,
      };
    } catch (error) {
      logger.error("Error getting current location:", error);
      return null;
    }
  }

  /**
   * Calculate distance between two coordinates in kilometers
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
  ): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) *
        Math.cos(this.toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Check if location has changed significantly (>= 10km)
   */
  private shouldUpdateLocation(newLocation: LocationCoordinates): boolean {
    const now = Date.now();
    const timeElapsed = now - this.lastUpdateTime;

    if (!this.lastLocation) {
      return true;
    }

    // Always update if it's been more than 2 minutes (heartbeat)
    if (timeElapsed > 120000) {
      return true;
    }

    // Don't update if it's been less than our minimum interval
    if (timeElapsed < this.minUpdateIntervalMs) {
      return false;
    }

    const distance = this.calculateDistance(
      this.lastLocation.latitude,
      this.lastLocation.longitude,
      newLocation.latitude,
      newLocation.longitude,
    );

    return distance >= this.updateIntervalKm;
  }

  /**
   * Update location for active booking
   */
  async updateBookingLocation(
    bookingId: number,
    location: LocationCoordinates,
  ): Promise<boolean> {
    const result = webSocketService.sendMessage("update_location", {
      user_id: useAuthStore.getState().user?.id,
      booking_id: bookingId,
      latitude: location.latitude,
      longitude: location.longitude,
      heading: location.heading,
      speed: location.speed,
      accuracy: location.accuracy,
    });
    if (result) {
      logger.debug("Booking location updated via WS");
      return true;
    } else {
      if (!this.shouldSendToApi("booking")) {
        return false;
      }

      try {
        await api.post("/location/update", {
          booking_id: bookingId,
          ...location,
        });
        this.lastApiUpdateTime = Date.now();
        this.apiErrorCount = 0;
        logger.debug("Booking location updated via API fallback");
        return true;
      } catch (error) {
        this.apiErrorCount++;
        logger.error(
          "Failed to update booking location via API fallback:",
          error,
        );
        return false;
      }
    }
  }

  /**
   * Handle background location updates
   */
  async handleBackgroundLocationUpdate(
    bookingId: number,
    coordinates: LocationCoordinates,
  ): Promise<void> {
    // Only process if it meets the update criteria
    if (!this.shouldUpdateLocation(coordinates)) {
      return;
    }

    // Add to batch
    this.locationBatch.push(coordinates);
    this.lastLocation = coordinates;
    this.lastUpdateTime = Date.now();

    // Send batch every when batch reaches 5 locations (reduced from 10)
    // or through the interval started in startBackgroundTracking
    if (this.locationBatch.length >= 5) {
      await this.sendLocationBatch(bookingId);
    }
  }

  /**
   * Send batched location updates to backend
   */
  private async sendLocationBatch(bookingId: number): Promise<void> {
    if (this.locationBatch.length === 0) {
      return;
    }

    const batch = [...this.locationBatch];
    this.locationBatch = [];

    try {
      // Send the latest location (most recent)
      const latest = batch[batch.length - 1];
      await this.updateBookingLocation(bookingId, latest);
      logger.debug(`Sent location batch of ${batch.length} locations`);
    } catch (error) {
      logger.error("Failed to send location batch:", error);
      // Re-add to batch if failed
      this.locationBatch.unshift(...batch);
    }
  }

  /**
   * Start background location tracking using Expo Location TaskManager
   */
  async startBackgroundTracking(bookingId: number): Promise<boolean> {
    if (this.isBackgroundTracking) {
      logger.debug("Background tracking already started");
      return true;
    }

    const hasPermission = await this.requestPermissions();
    if (!hasPermission) {
      logger.debug("Location permission not granted");
      return false;
    }

    try {
      // Store service instance globally for the background task to access
      (global as any).locationTrackingServiceInstance = this;

      // Check if task is defined (it should be defined at module level)
      if (!TaskManager.isTaskDefined(LOCATION_TASK_NAME)) {
        logger.error(
          "Background location task not defined. Make sure expo-task-manager is properly configured.",
        );
        return false;
      }

      await Location.startLocationUpdatesAsync(LOCATION_TASK_NAME, {
        accuracy: Location.Accuracy.Balanced,
        distanceInterval: 30, // Update every 30 meters
        timeInterval: 10000, // Check every 10 seconds
        foregroundService: {
          notificationTitle: "Location Tracking",
          notificationBody: "Tracking your location for the active booking",
        },
        pausesUpdatesAutomatically: false,
        activityType: Location.ActivityType.AutomotiveNavigation,
        showsBackgroundLocationIndicator: true,
      });

      if (this.locationBatch.length > 0) {
        this.sendLocationBatch(bookingId);
      }

      this.isBackgroundTracking = true;
      logger.debug("Background location tracking started");
      return true;
    } catch (error) {
      logger.error("Error starting background location tracking:", error);
      return false;
    }
  }

  /**
   * Stop background location tracking
   */
  async stopBackgroundTracking(): Promise<void> {
    if (!this.isBackgroundTracking) {
      return;
    }

    try {
      const isTaskRunning =
        await Location.hasStartedLocationUpdatesAsync(LOCATION_TASK_NAME);
      if (isTaskRunning) {
        await Location.stopLocationUpdatesAsync(LOCATION_TASK_NAME);
      }

      (global as any).locationTrackingServiceInstance = null;
      this.isBackgroundTracking = false;
      this.locationBatch = [];
      logger.debug("Background location tracking stopped");
    } catch (error) {
      logger.error("Error stopping background location tracking:", error);
    }
  }

  private handleWatchedLocation(
    coordinates: LocationCoordinates,
    onLocationUpdate?: (location: LocationCoordinates) => void,
  ): void {
    onLocationUpdate?.(coordinates);

    if (!this.shouldUpdateLocation(coordinates)) {
      return;
    }

    const activeBookingId = useAuthStore.getState().activeBooking;

    if (activeBookingId) {
      this.updateBookingLocation(activeBookingId, coordinates);
    } else {
      this.sendLocation(coordinates);
    }

    this.lastLocation = coordinates;
    this.lastUpdateTime = Date.now();
  }

  /**
   * Browser geolocation, because expo-location's web EventEmitter has no
   * removeSubscription. Calling watchPositionAsync().remove() throws and
   * blocks the online toggle.
   */
  private startWebLocationWatch(
    onLocationUpdate?: (location: LocationCoordinates) => void,
  ): void {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      throw new Error("Geolocation is not available in this browser");
    }

    this.webWatchId = navigator.geolocation.watchPosition(
      (position) => {
        this.handleWatchedLocation(
          {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            heading: position.coords.heading,
            speed: position.coords.speed || 0,
          },
          onLocationUpdate,
        );
      },
      (error) => {
        logger.error("Error watching location:", error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 15000,
      },
    );
  }

  private stopWebLocationWatch(): void {
    if (this.webWatchId == null || typeof navigator === "undefined") {
      this.webWatchId = null;
      return;
    }

    navigator.geolocation.clearWatch(this.webWatchId);
    this.webWatchId = null;
  }

  /**
   * Start tracking location with real-time updates for drivers
   */
  async startTracking(
    onLocationUpdate?: (location: LocationCoordinates) => void,
    bookingId?: number,
  ): Promise<boolean> {
    if (this.isTracking) {
      if (bookingId) {
        await this.startBackgroundTracking(bookingId);
      }

      logger.debug("Location tracking already started");
      return true;
    }

    const hasPermission = await this.requestPermissions();
    if (!hasPermission) {
      logger.debug("Location permission not granted");
      return false;
    }

    // If bookingId is provided, start background tracking
    if (bookingId) {
      await this.startBackgroundTracking(bookingId);
    }

    this.connectDispatch();

    try {
      if (Platform.OS === "web") {
        this.startWebLocationWatch(onLocationUpdate);
      } else {
        this.locationSubscription = await Location.watchPositionAsync(
          {
            accuracy: Location.Accuracy.High,
            distanceInterval: 20, // Check every 20 meters
            timeInterval: 5000, // Check every 5 seconds
          },
          (location) => {
            this.handleWatchedLocation(
              {
                latitude: location.coords.latitude,
                longitude: location.coords.longitude,
                accuracy: location.coords.accuracy,
                altitude: location.coords.altitude,
                heading: location.coords.heading,
                speed: location.coords.speed || 0,
              },
              onLocationUpdate,
            );
          },
        );
      }
      this.isTracking = true;
      return true;
    } catch (error) {
      logger.error("Error starting location tracking:", error);
      return false;
    }
  }

  /**
   * Stop tracking location
   */
  async stopTracking(): Promise<void> {
    if (Platform.OS === "web") {
      this.stopWebLocationWatch();
    } else if (this.locationSubscription) {
      this.locationSubscription.remove();
    }

    this.locationSubscription = null;

    await this.stopBackgroundTracking();

    this.isTracking = false;
    this.lastLocation = null;
  }

  /**
   * Set online status
   */
  async setOnlineStatus(isOnline: boolean): Promise<void> {
    await api.post("/location/online-status", { is_online: isOnline });
    logger.debug(`Online status set to: ${isOnline}`);
  }

  /**
   * Check if currently tracking
   */
  isCurrentlyTracking(): boolean {
    return this.isTracking;
  }

  /**
   * Get last known location
   */
  getLastLocation(): LocationCoordinates | null {
    return this.lastLocation;
  }
}

export const locationTrackingService = new LocationTrackingService();
